import { isInRegion } from '../types/map.types'

/**
 * Layered satellite imagery registry (feature 004).
 *
 * Single source of truth for every satellite tile service the app uses:
 * the interactive map, the PDF site-map generator, the offline downloader,
 * and the imagery audit script all consume this registry, so a source can
 * be added, re-calibrated, or removed in exactly one place.
 *
 * Contract: docs/specs/004-deep-zoom-imagery/contracts/imagery-source-registry.md
 * Endpoints: docs/specs/004-deep-zoom-imagery/contracts/external-tile-services.md
 *
 * The production service worker (vite-plugin-pwa generateSW) cannot import
 * this module — its tile cache routes duplicate these origins as literals in
 * vite.config.ts runtimeCaching. A unit test keeps the two lists in sync;
 * update vite.config.ts when changing origins here.
 */

export type ImagerySourceId = 'nsw' | 'qld' | 'tas' | 'linz' | 'esri-alp'

export interface ImagerySource {
  id: ImagerySourceId
  name: string
  /**
   * Raster XYZ template. Axis order is encoded in the template itself:
   * ArcGIS-style services are `.../{z}/{y}/{x}`, LINZ is `.../{z}/{x}/{y}`.
   * `{key}` marks where the API key is substituted (see buildTileUrl).
   */
  urlTemplate: string
  tileSize: 256
  /**
   * Shallowest zoom the layer is shown at. Regional sources floor at 10:
   * their low-zoom overview tiles render as white capture-program footprints
   * (not imagery), so below the floor the Esri fallback owns the view alone —
   * which also halves the tile load when zooming far out.
   */
  minZoom: number
  /** Deepest zoom with real tiles; Leaflet over-zooms beyond it. Calibrated by the imagery audit. */
  maxNativeZoom: number
  /** HTML credit line for the Leaflet attribution control. */
  attribution: string
  /** Plain-text credit rendered onto the PDF site-map canvas. */
  attributionPlain: string
  licence: string
  /** Only sources whose licence permits bulk download may be used offline. */
  offlinePermitted: boolean
  requiresKey: 'arcgis' | 'linz' | null
  coverage: (lat: number, lng: number) => boolean
  role: 'regional' | 'fallback'
  enabled: boolean
}

export interface ResolvedImagery {
  primary: ImagerySource
  /** Fallback layer rendered beneath the primary; null when primary IS the fallback. */
  underlay: ImagerySource | null
  effectiveMaxZoom: number
}

/**
 * Web Mercator lon/lat -> fractional tile coordinates at a zoom level
 * (callers Math.floor for the integer tile index). Single shared
 * implementation —
 * used by the static site-map compositor, the offline downloader's tile
 * enumeration, and the imagery audit script, so all of them address the
 * exact tiles the app requests.
 */
export function lonToTileX(lon: number, zoom: number): number {
  return ((lon + 180) / 360) * Math.pow(2, zoom)
}

export function latToTileY(lat: number, zoom: number): number {
  const rad = (lat * Math.PI) / 180
  return ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * Math.pow(2, zoom)
}

function getKey(kind: 'arcgis' | 'linz'): string {
  const value =
    kind === 'arcgis'
      ? (import.meta.env.VITE_ARCGIS_API_KEY as string | undefined)
      : (import.meta.env.VITE_LINZ_API_KEY as string | undefined)
  return value ?? ''
}

/**
 * Registry order is the resolution precedence: REGION_BOUNDARIES boxes are
 * approximate rectangles that overlap (NSW/QLD share a latitude band, NSW's
 * box reaches into northern VIC), and the first enabled match wins — same
 * behaviour as the cadastre overlay's detectRegion. A regional source asked
 * for tiles outside its real data extent returns empty tiles and the
 * fallback underlay shows through.
 */
export const IMAGERY_SOURCES: readonly ImagerySource[] = [
  {
    id: 'nsw',
    name: 'NSW Spatial Services Imagery',
    urlTemplate:
      'https://maps.six.nsw.gov.au/arcgis/rest/services/public/NSW_Imagery/MapServer/tile/{z}/{y}/{x}',
    tileSize: 256,
    minZoom: 10,
    maxNativeZoom: 21,
    attribution:
      '© <a href="https://www.spatial.nsw.gov.au/">State Government of NSW and Spatial Services (DCS)</a>',
    attributionPlain: 'Imagery © State Government of NSW and Spatial Services (DCS)',
    licence: 'CC BY 3.0 AU',
    offlinePermitted: true,
    requiresKey: null,
    coverage: (lat, lng) => isInRegion(lat, lng, 'NSW') || isInRegion(lat, lng, 'ACT'),
    role: 'regional',
    enabled: true,
  },
  {
    id: 'qld',
    name: 'QLD Latest State Program Imagery',
    urlTemplate:
      'https://spatial-img.information.qld.gov.au/arcgis/rest/services/Basemaps/LatestStateProgram_AllUsers/ImageServer/tile/{z}/{y}/{x}',
    tileSize: 256,
    minZoom: 10,
    maxNativeZoom: 20,
    attribution:
      '© <a href="https://www.resources.qld.gov.au/">State of Queensland (Department of Resources)</a>',
    attributionPlain: 'Imagery © State of Queensland (Department of Resources)',
    licence: 'CC BY 4.0',
    offlinePermitted: true,
    requiresKey: null,
    coverage: (lat, lng) => isInRegion(lat, lng, 'QLD'),
    role: 'regional',
    enabled: true,
  },
  {
    // Disabled until the theLIST tile-service licence is confirmed
    // (open item #1 in contracts/external-tile-services.md). TAS resolves
    // to the fallback meanwhile.
    id: 'tas',
    name: 'theLIST Tasmania Orthophoto',
    urlTemplate:
      'https://services.thelist.tas.gov.au/arcgis/rest/services/Basemaps/Orthophoto/MapServer/tile/{z}/{y}/{x}',
    tileSize: 256,
    minZoom: 10,
    maxNativeZoom: 19,
    attribution: '© <a href="https://www.thelist.tas.gov.au/">LIST Tasmania</a>',
    attributionPlain: 'Imagery © LIST Tasmania',
    licence: 'CC BY 3.0 AU (unconfirmed for tile service)',
    offlinePermitted: true,
    requiresKey: null,
    coverage: (lat, lng) => isInRegion(lat, lng, 'TAS'),
    role: 'regional',
    enabled: false,
  },
  {
    id: 'linz',
    name: 'LINZ Basemaps Aerial (NZ)',
    urlTemplate:
      'https://basemaps.linz.govt.nz/v1/tiles/aerial/WebMercatorQuad/{z}/{x}/{y}.webp?api={key}',
    tileSize: 256,
    minZoom: 10,
    maxNativeZoom: 21,
    attribution: '© <a href="https://www.linz.govt.nz/">LINZ</a> CC BY 4.0',
    attributionPlain: 'Imagery © LINZ CC BY 4.0 — Sourced from LINZ Data Service',
    licence: 'CC BY 4.0',
    offlinePermitted: true,
    requiresKey: 'linz',
    coverage: (lat, lng) => isInRegion(lat, lng, 'NZ'),
    role: 'regional',
    enabled: true,
  },
  {
    id: 'esri-alp',
    name: 'Esri World Imagery (ArcGIS Location Platform)',
    urlTemplate:
      'https://ibasemaps-api.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}?token={key}',
    tileSize: 256,
    minZoom: 2,
    // Audit 2026-09-09: real z20 in AU metro (Sydney/Brisbane/Melbourne), but
    // 404s from z19 in some regional areas (Bathurst, Busselton) — the
    // authenticated endpoint 404s instead of serving placeholder tiles, so
    // the tile layer MUST have per-tile parent-zoom fallback (see
    // offlineTileLayer) to use this depth without holes.
    maxNativeZoom: 20,
    attribution:
      'Powered by <a href="https://www.esri.com/">Esri</a> — Esri, Vantor, Earthstar Geographics, and the GIS User Community',
    attributionPlain: 'Imagery © Esri — Esri, Vantor, Earthstar Geographics, GIS User Community',
    licence: 'Esri Master Agreement (ArcGIS Location Platform)',
    offlinePermitted: true,
    requiresKey: 'arcgis',
    coverage: () => true,
    role: 'fallback',
    enabled: true,
  },
]

function sourceUsable(source: ImagerySource): boolean {
  if (!source.enabled) return false
  // The fallback stays usable without its key (see buildTileUrl); regional
  // key-gated sources are excluded so callers never build broken URLs.
  if (source.role === 'regional' && source.requiresKey && !getKey(source.requiresKey)) return false
  return true
}

export function resolveImagerySource(lat: number, lng: number): ResolvedImagery {
  const fallback = IMAGERY_SOURCES.find((s) => s.role === 'fallback' && s.enabled)
  if (!fallback) throw new Error('imagery registry has no enabled fallback source')

  const regional = IMAGERY_SOURCES.find(
    (s) => s.role === 'regional' && sourceUsable(s) && s.coverage(lat, lng)
  )

  if (!regional) {
    return { primary: fallback, underlay: null, effectiveMaxZoom: fallback.maxNativeZoom }
  }
  return {
    primary: regional,
    underlay: fallback,
    effectiveMaxZoom: Math.max(regional.maxNativeZoom, fallback.maxNativeZoom),
  }
}

export function buildTileUrl(source: ImagerySource, z: number, x: number, y: number): string {
  let url = source.urlTemplate
    .replace('{z}', String(z))
    .replace('{x}', String(x))
    .replace('{y}', String(y))

  if (source.requiresKey) {
    const key = getKey(source.requiresKey)
    if (key) {
      url = url.replace('{key}', encodeURIComponent(key))
    } else if (source.role === 'fallback') {
      // Keyless fallback must still render (the endpoint currently serves
      // without token validation); strip the key parameter and complain.
      console.error(
        `imagerySources: ${source.id} API key is not set — requesting without a token. ` +
          'Set VITE_ARCGIS_API_KEY; unauthenticated access is not guaranteed to keep working.'
      )
      url = url.replace(/[?&](token|api)=\{key\}/, '')
    } else {
      throw new Error(
        `imagerySources: ${source.id} requires an API key that is not configured — ` +
          'resolveImagerySource should have filtered this source out'
      )
    }
  }
  return url
}

export interface SatelliteLayerSpec {
  source: ImagerySource
  options: {
    minZoom: number
    maxZoom: number
    maxNativeZoom: number
    zIndex: number
    attribution: string
    crossOrigin: true
    updateWhenZooming: false
  }
}

export interface SatelliteLayersConfig {
  /** Bottom-first: underlay (if any) then primary. */
  layers: SatelliteLayerSpec[]
  /** Map maxZoom: deepest native tile + 2 levels of over-zoom headroom. */
  mapMaxZoom: number
}

/** Over-zoom headroom beyond the deepest native tiles, matching pre-004 behaviour. */
const OVER_ZOOM_HEADROOM = 2

/**
 * Pure derivation of the Leaflet satellite layer stack from a resolution.
 * The underlay absorbs regional-source gaps at tile granularity; both layers
 * over-zoom to the shared ceiling so neither ever goes blank at deep zoom.
 */
export function buildSatelliteLayerConfig(resolved: ResolvedImagery): SatelliteLayersConfig {
  const mapMaxZoom = resolved.effectiveMaxZoom + OVER_ZOOM_HEADROOM
  const spec = (source: ImagerySource, zIndex: number): SatelliteLayerSpec => ({
    source,
    options: {
      minZoom: source.minZoom,
      maxZoom: mapMaxZoom,
      maxNativeZoom: source.maxNativeZoom,
      zIndex,
      attribution: source.attribution,
      crossOrigin: true,
      // Never load tile levels mid-gesture: with the default (true), every
      // integer level a fast pinch sweeps through creates-then-aborts a full
      // viewport of tiles per layer — enough churn to crash mobile WebKit.
      // Tiles load when the gesture settles instead.
      updateWhenZooming: false,
    },
  })
  const layers = resolved.underlay
    ? [spec(resolved.underlay, 1), spec(resolved.primary, 2)]
    : [spec(resolved.primary, 1)]
  return { layers, mapMaxZoom }
}
